import type { IUser } from "@/domains/shared/models/IUser";
import {
    THERAPIST_PROFILE_ADDRESS_HASH,
    THERAPIST_PROFILE_CPF_HASH,
    THERAPIST_PROFILE_PHONE_HASH,
} from "@/domains/subscription/accountAccess";
import { SUBSCRIPTION_TRANSLATIONS } from "@/domains/subscription/constants";
import { getTranslation } from "@mivo/i18n";

export type CreditCardHolderPayload = {
    name: string;
    email: string;
    cpfCnpj: string;
    postalCode: string;
    addressNumber: string;
    addressComplement?: string | null;
    phone?: string | null;
    mobilePhone: string;
};

export type CardHolderNameInput = {
    holderName: string;
};

export type CardBillingProfileGapId = "name" | "email" | "cpf" | "phone" | "address";

export type CardBillingProfileGap = {
    id: CardBillingProfileGapId;
    label: string;
    profileHash: string;
};

type CardBillingGapLabelKey =
    | "cardBillingGapFullName"
    | "cardBillingGapEmail"
    | "cardBillingGapCpf"
    | "cardBillingGapPhone"
    | "cardBillingGapAddress"
    | "cardBillingGapPostalCode"
    | "cardBillingGapStreetNumber";

const GAP_PROFILE_HASHES: Record<CardBillingProfileGapId, string> = {
    name: "#profile-name-input",
    email: "#profile-name-input",
    cpf: THERAPIST_PROFILE_CPF_HASH,
    phone: THERAPIST_PROFILE_PHONE_HASH,
    address: THERAPIST_PROFILE_ADDRESS_HASH,
};

const DEFAULT_GAP_LABEL_KEYS: Record<CardBillingProfileGapId, CardBillingGapLabelKey> = {
    name: "cardBillingGapFullName",
    email: "cardBillingGapEmail",
    cpf: "cardBillingGapCpf",
    phone: "cardBillingGapPhone",
    address: "cardBillingGapAddress",
};

function gapLabel(labelKey: CardBillingGapLabelKey): string {
    return getTranslation(SUBSCRIPTION_TRANSLATIONS[labelKey]);
}

function gap(id: CardBillingProfileGapId, labelKey?: CardBillingGapLabelKey): CardBillingProfileGap {
    return {
        id,
        label: gapLabel(labelKey ?? DEFAULT_GAP_LABEL_KEYS[id]),
        profileHash: GAP_PROFILE_HASHES[id],
    };
}

function digitsOnly(value: string | null | undefined): string {
    return (value ?? "").replace(/\D/g, "");
}

function pickText(...values: Array<string | null | undefined>): string {
    for (const value of values) {
        const trimmed = value?.trim();
        if (trimmed) {
            return trimmed;
        }
    }

    return "";
}

function hasValidCpf(user: IUser): boolean {
    return user.hasCpf === true;
}

function hasValidPhone(user: IUser): boolean {
    return user.hasPhone === true;
}

function pushGap(gaps: CardBillingProfileGap[], next: CardBillingProfileGap) {
    if (!gaps.some((g) => g.id === next.id)) {
        gaps.push(next);
    }
}

/** Ordered gaps blocking card billing; first item is the Ir agora scroll target. */
export function getCardBillingProfileGaps(user: IUser | null | undefined): CardBillingProfileGap[] {
    if (!user) {
        return [gap("cpf"), gap("phone"), gap("address")];
    }

    const gaps: CardBillingProfileGap[] = [];

    if (!pickText(user.name)) {
        pushGap(gaps, gap("name"));
    }

    if (!pickText(user.email)) {
        pushGap(gaps, gap("email"));
    }

    if (!hasValidCpf(user)) {
        pushGap(gaps, gap("cpf"));
    }

    if (!hasValidPhone(user)) {
        pushGap(gaps, gap("phone"));
    }

    if (!user.hasAddress) {
        pushGap(gaps, gap("address"));
    } else if (!pickText(user.postalCode)) {
        pushGap(gaps, gap("address", "cardBillingGapPostalCode"));
    } else if (!pickText(user.streetNumber)) {
        pushGap(gaps, gap("address", "cardBillingGapStreetNumber"));
    }

    return gaps;
}

export function formatCardBillingProfileGapsList(gaps: CardBillingProfileGap[]): string {
    return gaps.map((g) => g.label).join(", ");
}

export function profileHashForFirstCardBillingGap(gaps: CardBillingProfileGap[]): string {
    return gaps[0]?.profileHash ?? THERAPIST_PROFILE_ADDRESS_HASH;
}

export const CARD_BILLING_PROFILE_SCROLL_HASHES = [
    "#profile-name-input",
    THERAPIST_PROFILE_PHONE_HASH,
    THERAPIST_PROFILE_CPF_HASH,
    THERAPIST_PROFILE_ADDRESS_HASH,
] as const;

export function isCardBillingProfileScrollHash(hash: string): boolean {
    const normalized = hash.toLowerCase();
    return CARD_BILLING_PROFILE_SCROLL_HASHES.some((target) => normalized.includes(target.slice(1)));
}

/** Profile fields required for Asaas credit-card holder info (server also enriches from DB). */
export function hasCardBillingProfile(user: IUser | null | undefined): user is IUser {
    return getCardBillingProfileGaps(user).length === 0;
}

export function buildCreditCardHolderPayload(
    user: IUser,
    card: CardHolderNameInput,
): CreditCardHolderPayload {
    const postalCode = digitsOnly(user.postalCode);

    return {
        name: pickText(card.holderName, user.name),
        email: pickText(user.email),
        cpfCnpj: "",
        postalCode,
        addressNumber: pickText(user.streetNumber),
        addressComplement: null,
        phone: null,
        mobilePhone: "",
    };
}

function collectGatewayErrorTexts(error: unknown): string[] {
    const ax = error as {
        response?: { data?: { errors?: Array<{ description?: string }>; detail?: string } };
    };
    const texts: string[] = [];
    const detail = ax.response?.data?.detail?.trim();
    if (detail) {
        texts.push(detail);
    }

    const errors = ax.response?.data?.errors;
    if (Array.isArray(errors)) {
        for (const err of errors) {
            const description = err.description?.trim();
            if (description) {
                texts.push(description);
            }
        }
    }

    return texts;
}

/** Maps gateway validation text to profile gaps; empty when error is not profile-related. */
export function getCardBillingProfileGapsFromGatewayError(error: unknown): CardBillingProfileGap[] {
    const gaps: CardBillingProfileGap[] = [];

    for (const text of collectGatewayErrorTexts(error)) {
        const lower = text.toLowerCase();

        if (/cpf/.test(lower)) {
            pushGap(gaps, gap("cpf"));
            continue;
        }

        if (/celular|contato com ddd|telefone/.test(lower)) {
            pushGap(gaps, gap("phone"));
            continue;
        }

        if (/cep|endere[cç]o|n(ú|u)mero do endere[cç]o/.test(lower)) {
            pushGap(gaps, gap("address"));
        }
    }

    return gaps;
}
